/**
 * Opt-in console tracing of the SmartLib (Broadpeak CDN) session lifecycle: session create /
 * retire / stop with the reason, getURL results, player attach, and every stop request that was
 * ignored because the caller does not own the session.
 *
 * Off by default, and switchable at runtime so it also works on staging / production builds:
 * - `?cdnDebug=1` in the URL (remembered until `?cdnDebug=0`)
 * - `localStorage.setItem('stctv:cdn-debug', '1')` and reload
 * - `window.cdnDebug.enable()` in the console; `window.cdnDebug.state()` prints the current sessions
 *
 * Sessions and players get short ids (`s3`, `p2`) and sessions show their bpk-token once resolved,
 * so log lines can be matched against `bpk-token` requests in the Network panel.
 */
const STORAGE_KEY = 'stctv:cdn-debug';
const URL_PARAM = 'cdnDebug';

const readStorage = (): string | null => {
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

const writeStorage = (value: string | null): void => {
    try {
        if (value === null) window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
        // Storage unavailable (private mode, blocked site data): the flag lasts for this page only.
    }
};

const readInitialFlag = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const fromUrl = new URLSearchParams(window.location.search).get(URL_PARAM);
        if (fromUrl === '1' || fromUrl === '0') writeStorage(fromUrl === '1' ? '1' : null);
    } catch {
        // Malformed location: fall back to storage.
    }
    return readStorage() === '1';
};

let enabled = readInitialFlag();

const ids = new WeakMap<object, string>();
const tokens = new WeakMap<object, string>();
let sessionCount = 0;
let playerCount = 0;

const idOf = (obj: object | null | undefined, prefix: 's' | 'p'): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    let id = ids.get(obj);
    if (!id) {
        id = prefix === 's' ? `s${++sessionCount}` : `p${++playerCount}`;
        ids.set(obj, id);
    }
    return id;
};

/** `s3(4ar@k2j4…)` / `s3 stopped` / `none` — the session as it appears in the logs. */
export const describeSession = (session: any): string => {
    if (!session) return 'none';
    const token = tokens.get(session);
    return `${idOf(session, 's')}${token ? `(${token})` : ''}${session.stopped ? ' stopped' : ''}`;
};

/** `p2` / `none` — the player as it appears in the logs. */
export const describePlayer = (player: object | null | undefined): string => idOf(player, 'p') ?? 'none';

/** Remembers the bpk-token a session resolved to, so later log lines show it. */
export const rememberSessionToken = (session: object | null | undefined, resolvedUrl: string): void => {
    if (!session || typeof session !== 'object') return;
    const token = String(resolvedUrl).match(/bpk-token\/([^/]+)/)?.[1];
    if (token) tokens.set(session, decodeURIComponent(token).slice(0, 16) + '…');
};

/** Shortens a stream URL to host + path for logging (no query: tokens and auth stay out of the console). */
export const shortUrl = (value: string): string => {
    try {
        const parsed = new URL(value);
        return parsed.host + parsed.pathname.replace(/\/bpk-token\/[^/]+/, '/bpk-token/…');
    } catch {
        return String(value).split('?')[0];
    }
};

const stoppedPlayers = new WeakSet<object>();

/** Records that a player's session was stopped, so a later stop request for it reads as "already stopped". */
export const noteSessionStopped = (player: object | null | undefined): void => {
    if (player && typeof player === 'object') stoppedPlayers.add(player);
};

export const wasSessionStopped = (player: object | null | undefined): boolean =>
    !!player && typeof player === 'object' && stoppedPlayers.has(player);

export const isCdnDebugEnabled = (): boolean => enabled;

/** Logs one session lifecycle event when tracing is enabled. */
export const cdnLog = (event: string, details?: Record<string, unknown>): void => {
    if (!enabled) return;
    const time = new Date().toISOString().slice(11, 23);
    console.info(`%c[CDN ${time}] ${event}`, 'color:#0b7d6e;font-weight:600', details ?? '');
};

let stateProvider: (() => unknown) | null = null;

/** Lets the handler expose a snapshot of its sessions to `window.cdnDebug.state()`. */
export const registerCdnDebugState = (provider: () => unknown): void => {
    stateProvider = provider;
};

if (typeof window !== 'undefined') {
    (window as any).cdnDebug = {
        enable: () => {
            enabled = true;
            writeStorage('1');
            return 'CDN session tracing on (kept across reloads; window.cdnDebug.disable() to stop)';
        },
        disable: () => {
            enabled = false;
            writeStorage(null);
            return 'CDN session tracing off';
        },
        isEnabled: () => enabled,
        state: () => stateProvider?.() ?? null,
    };
}
